import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { MapPin, Phone, Eye, Calendar, Share2, Flag, Heart, User, Star, TrendingUp, ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import ListingCard from '../components/ListingCard';
import ContactAdvertiser from '../components/ContactAdvertiser';
import SellerReviews from '../components/SellerReviews';
import VerifiedBadge from '../components/VerifiedBadge';
import ReportDialog from '../components/ReportDialog';
import VehicleSpecsTabs from '../components/VehicleSpecsTabs';
import AutoPartSpecsTabs from '../components/AutoPartSpecsTabs';
import AutoPartsServices from '../components/AutoPartsServices';
import HomeGardenServices from '../components/HomeGardenServices';
import SportsFitnessServices from '../components/SportsFitnessServices';
import MusicalInstrumentsServices from '../components/MusicalInstrumentsServices';
import FinanceThisCar from '../components/FinanceThisCar';
import PayByCash from '../components/PayByCash';
import NearbyVehicleServices from '../components/NearbyVehicleServices';
import InsureThisCar from '../components/InsureThisCar';
import InsureJewellery from '../components/InsureJewellery';
import NextBestActions from '../components/NextBestActions';
import PayMonthlyProviders from '../components/services/PayMonthlyProviders';
import NearbyAmenitiesMap, { AmenitiesList } from '../components/NearbyAmenitiesMap';
import PropertyServices from '../components/PropertyServices';
import { useCountry } from '../context/CountryContext';
import axios from 'axios';

const ListingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { countryConfig, country: visitorCountry } = useCountry();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedListings, setRelatedListings] = useState([]);
  const [sellerVerification, setSellerVerification] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showPhone, setShowPhone] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportType, setReportType] = useState('listing');

  // Currency should always match the LISTING's country, not the visitor's context.
  const listingCountryCode = (listing?.country || '').toUpperCase();
  const currencySymbol = listingCountryCode === 'LK' || listingCountryCode === 'SL'
    ? 'Rs.'
    : listingCountryCode === 'UK' || listingCountryCode === 'GB'
      ? '£'
      : (countryConfig?.currency_symbol || 'Rs.');

  // Cross-country guard: visitor's site country vs the listing's country.
  const visitorCountryNorm = (visitorCountry || '').toUpperCase() === 'SL' ? 'LK' : (visitorCountry || '').toUpperCase();
  const listingCountryNorm = listingCountryCode === 'SL' ? 'LK' : listingCountryCode;
  const isCrossCountry = !!listing && !!visitorCountryNorm && !!listingCountryNorm && visitorCountryNorm !== listingCountryNorm;
  const listingCountryName = listingCountryNorm === 'LK' ? 'Sri Lanka' : listingCountryNorm === 'UK' ? 'the United Kingdom' : listingCountryNorm;

  // Seller reviews accordion - expanded state lifted so the compact preview
  // inside ContactAdvertiser can trigger the full-width card below.
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const [sellerRatingSummary, setSellerRatingSummary] = useState(null);
  const reviewsCardRef = useRef(null);

  const handleOpenReviews = () => {
    setReviewsExpanded(true);
    setTimeout(() => {
      reviewsCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  useEffect(() => {
    fetchListing();
  }, [id]);

  // Track view when listing loads
  useEffect(() => {
    if (listing?.id) {
      trackView(listing.id);
    }
  }, [listing?.id]);

  const trackView = async (listingId) => {
    try {
      // Get or create visitor ID
      let visitorId = localStorage.getItem('visitor_id');
      if (!visitorId) {
        visitorId = 'v_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('visitor_id', visitorId);
      }
      
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/analytics/track/view`,
        { listing_id: listingId, visitor_id: visitorId }
      );
    } catch (error) {
      // Silent fail - analytics shouldn't break the app
      console.debug('Analytics tracking failed:', error);
    }
  };

  const fetchListing = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/listings/${id}`
      );
      setListing(response.data);
      
      // Fetch seller's verification status
      const sellerId = response.data.user_id || response.data.seller?.id;
      if (sellerId) {
        try {
          const verifyResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/verification/user/${sellerId}`
          );
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            setSellerVerification(verifyData);
          }
        } catch (e) {
          console.debug('Could not fetch seller verification:', e);
        }
      }
      
      // Fetch related listings — always scoped to the listing's own country
      if (response.data.category) {
        fetchRelatedListings(response.data.category, response.data.country);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching listing:', err);
      setError(err.response?.status === 404 ? 'Listing not found' : 'Failed to load listing');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedListings = async (category, country) => {
    try {
      // Related Ads must respect the listing's own country so an LK visitor
      // viewing a UK listing sees other UK listings (never a mixed grid).
      const params = new URLSearchParams({ category, limit: '4' });
      if (country) params.set('country_code', country);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/listings/?${params.toString()}`
      );
      // Filter out current listing
      const filtered = response.data.listings.filter(l => l.id !== id);
      setRelatedListings(filtered.slice(0, 4));
    } catch (err) {
      console.error('Error fetching related listings:', err);
    }
  };

  // Check if listing is in favorites on load
  useEffect(() => {
    checkIfFavorite();
  }, [id]);

  const checkIfFavorite = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/favorites/check/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsFavorite(response.data.is_favorited);
    } catch (err) {
      console.error('Error checking favorites:', err);
    }
  };

  const toggleFavorite = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to add to favorites');
      navigate('/login', { state: { from: `/listing/${id}` } });
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/favorites/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsFavorite(false);
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/favorites/`,
          { listing_id: id, price_alert_enabled: false },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsFavorite(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      alert('Failed to update favorites');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: listing.title,
        text: listing.description,
        url: window.location.href,
      }).catch(() => {
        // Fallback to clipboard
        copyToClipboard();
      });
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === listing.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? listing.images.length - 1 : prev - 1
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading listing...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">{error}</h2>
            <p className="text-gray-600 mb-6">The listing you're looking for doesn't exist or has been removed.</p>
            <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-blue-600 to-purple-600">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!listing) return null;

  const images = listing.images && listing.images.length > 0
    ? listing.images.map(img => (img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`))
    : ['https://via.placeholder.com/800x600?text=No+Image'];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Cross-country visitor guard: this listing is from another country */}
        {isCrossCountry && (
          <div
            className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            role="alert"
            data-testid="cross-country-banner"
          >
            <span className="text-xl leading-none">🌐</span>
            <div className="flex-1">
              <span className="font-semibold">This listing is in {listingCountryName}.</span>{' '}
              You're browsing our {visitorCountryNorm === 'LK' ? 'Sri Lanka' : 'UK'} site, so
              contacting the seller and shipping/services shown apply to {listingCountryName} only.
            </div>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              data-testid="cross-country-banner-back"
            >
              Back to {visitorCountryNorm === 'LK' ? 'Sri Lanka' : 'UK'} listings
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Carousel */}
            <Card className="overflow-hidden">
              <div className="relative">
                <img
                  src={images[currentImageIndex]}
                  alt={listing.title}
                  className="w-full h-96 object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/800x600?text=No+Image';
                  }}
                />
                
                {/* Badges */}
                {listing.featured && (
                  <Badge className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0">
                    Featured
                  </Badge>
                )}
                {listing.promoted && (
                  <Badge className="absolute top-4 right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0 flex items-center space-x-1">
                    <TrendingUp className="h-3 w-3" />
                    <span>Promoted</span>
                  </Badge>
                )}

                {/* Image Navigation */}
                {images.length > 1 && (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 hover:bg-white"
                      onClick={prevImage}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 hover:bg-white"
                      onClick={nextImage}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    
                    {/* Image Counter */}
                    <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnail Strip */}
              {images.length > 1 && (
                <div className="p-4 flex gap-2 overflow-x-auto">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                        index === currentImageIndex ? 'border-blue-600' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-20 h-20 object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </Card>

            {/* Details */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {listing.is_demo && (
                        <span
                          className="inline-block align-middle mr-2 px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide bg-gray-200 text-gray-600 border border-gray-300"
                          data-testid="demo-pill-detail"
                        >
                          Demo
                        </span>
                      )}
                      {listing.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        <span>{listing.location}</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>Posted {new Date(listing.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center">
                        <Eye className="h-4 w-4 mr-1" />
                        <span>{listing.views || 0} views</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={toggleFavorite}
                      className="rounded-full"
                    >
                      <Heart className={`h-5 w-5 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={handleShare}
                      className="rounded-full"
                    >
                      <Share2 className="h-5 w-5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => {
                        setReportType('listing');
                        setShowReportDialog(true);
                      }}
                      title="Report Listing"
                    >
                      <Flag className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                {/* Feature Ad Button - hidden for demo listings */}
                {!listing.is_demo && !listing.featured && (
                  <Button
                    onClick={() => navigate('/feature-ad', { 
                      state: { listingId: listing._id || id, listingTitle: listing.title } 
                    })}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-6 text-lg font-bold shadow-lg mt-4"
                  >
                    <Star className="h-5 w-5 mr-2" />
                    ⭐ Feature This Ad - Get 5x More Views!
                  </Button>
                )}
                {listing.is_demo && (
                  <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-testid="demo-payment-disabled-note">
                    <span className="font-semibold text-gray-700">Demo listing:</span> Paid upgrades (Featured/Boost) are disabled on demo listings.
                  </div>
                )}
                {listing.featured && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-yellow-100 to-orange-100 border-2 border-yellow-500 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Star className="h-5 w-5 text-yellow-600 fill-yellow-600" />
                      <span className="font-bold text-yellow-900">This ad is currently featured!</span>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                </div>

                <Separator className="my-4" />

                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-1">Price</h3>
                    <div className="flex items-baseline space-x-2">
                      <span className="text-3xl font-bold text-blue-600">
                        {currencySymbol} {parseInt(listing.price).toLocaleString()}
                      </span>
                      {listing.negotiable && (
                        <Badge variant="outline" className="text-green-600 border-green-600">
                          Negotiable
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Category</h3>
                      <p className="text-gray-600 capitalize">{listing.category}</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Condition</h3>
                      <p className="text-gray-600">{listing.condition}</p>
                    </div>
                  </div>
                </div>

                <Separator className="my-6" />

                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Description</h3>
                  <p className="text-gray-600 whitespace-pre-line">{listing.description}</p>
                </div>
              </CardContent>
            </Card>

            {/* Vehicle Services Stack - Specs Tabs, Next Best Actions, Finance, Insurance, Pay by Cash */}
            {/* Uses parent_category (reliable) instead of substring-matching the sub-category slug,
                which previously hid the entire stack for boats, vans, buses, three-wheelers, etc. */}
            {listing.parent_category === 'vehicles' && (
              <div className="space-y-6" data-testid="vehicle-services-stack">
                {listing.is_demo && (
                  <Alert className="border-amber-200 bg-amber-50" data-testid="vehicle-services-demo-notice">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-sm text-amber-800">
                      <Badge variant="secondary" className="mr-2 bg-amber-100 text-amber-800 border-amber-200">DEMO</Badge>
                      Finance quotes, insurance premiums and vehicle-history checks below are illustrative
                      for this demo listing. Live quotes appear on real seller listings.
                    </AlertDescription>
                  </Alert>
                )}
                <VehicleSpecsTabs listing={listing} />
                {/* AI-Powered Next Best Actions */}
                <NextBestActions listing={listing} userStage="browsing" isDemo={!!listing.is_demo} />
                <div id="finance">
                  <FinanceThisCar listing={listing} isDemo={!!listing.is_demo} />
                </div>
                <div id="insurance">
                  <InsureThisCar listing={listing} isDemo={!!listing.is_demo} />
                </div>
                {/* Nearby garages / MOT / tyres / detailing / breakdown */}
                <NearbyVehicleServices listing={listing} isDemo={!!listing.is_demo} />
                <PayByCash listing={listing} isDemo={!!listing.is_demo} />
              </div>
            )}

            {/* Auto Parts Specifications Tabs - Overview + Fitting & Ownership */}
            {listing.parent_category === 'auto-parts-accessories' && (
              <>
                <AutoPartSpecsTabs listing={listing} />
                <AutoPartsServices listing={listing} />
              </>
            )}

            {/* Home & Garden Delivery services */}
            {listing.parent_category === 'home-garden' && (
              <HomeGardenServices listing={listing} />
            )}

            {/* Sports, Fitness & Hobbies Delivery services */}
            {listing.parent_category === 'sports-fitness-hobbies' && (
              <SportsFitnessServices listing={listing} />
            )}

            {/* Musical Instruments Delivery + optional Finance for UK gear ≥ £2000 */}
            {listing.parent_category === 'musical-instruments' && (
              <MusicalInstrumentsServices listing={listing} />
            )}

            {/* Jewellery Insurance & Loans Section */}
            {listing.category && (listing.category.toLowerCase().includes('jewellery') || listing.category.toLowerCase().includes('watches') || listing.category.toLowerCase() === 'jewellery-watches') && (
              <>
                <div id="jewellery-services">
                  <InsureJewellery listing={listing} />
                </div>
                <PayByCash listing={listing} />
              </>
            )}

            {/* Sports & Cycling Equipment Pay Monthly Section */}
            {listing.subcategory && ['sports-equipment', 'cycling-equipment'].includes(listing.subcategory) && (
              <div id="pay-monthly-services" data-testid="sports-pay-monthly-section">
                <PayMonthlyProviders listing={listing} />
              </div>
            )}

            {/* Nearby Amenities Section for Property Listings */}
            {listing.category && listing.category.toLowerCase() === 'property' && listing.latitude && listing.longitude && (
              <Card data-testid="nearby-amenities-section">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    Nearby Amenities
                  </h3>
                  <div className="h-[400px] rounded-lg overflow-hidden mb-4">
                    <NearbyAmenitiesMap
                      propertyLat={listing.latitude}
                      propertyLon={listing.longitude}
                      propertyTitle={listing.title}
                      className="h-full"
                    />
                  </div>
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-3">Key Amenities Nearby</h4>
                    <AmenitiesList 
                      propertyLat={listing.latitude}
                      propertyLon={listing.longitude}
                      radius={1000}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Property Services Section - Valuations, Mortgages, Legal, Insurance */}
            <PropertyServices listing={listing} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Advertiser Card (compact reviews preview inline) */}
            <ContactAdvertiser
              listing={listing}
              ratingSummary={sellerRatingSummary}
              onOpenReviews={handleOpenReviews}
            />

            {/* Full seller reviews accordion - click chevron in Contact card
                jumps here and auto-expands. */}
            {(listing.user_id || listing.seller?.id) && (
              <div ref={reviewsCardRef} data-testid="seller-reviews-anchor">
                <SellerReviews
                  sellerId={listing.user_id || listing.seller?.id}
                  sellerName={listing.seller_name || listing.seller?.name || listing.user_name || 'Seller'}
                  listingId={listing.id || listing._id}
                  listingCountry={listing.country}
                  expanded={reviewsExpanded}
                  onExpandedChange={setReviewsExpanded}
                  onSummaryLoaded={setSellerRatingSummary}
                />
              </div>
            )}
          </div>
        </div>

        {/* Related Listings */}
        {relatedListings.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">Related Ads</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedListings.map(listing => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report Dialog */}
      <ReportDialog
        open={showReportDialog}
        onClose={() => setShowReportDialog(false)}
        type={reportType}
        targetId={reportType === 'listing' ? (listing?.id || id) : (listing?.user_id || listing?.seller?.id)}
        targetName={reportType === 'listing' ? listing?.title : (listing?.seller?.name || 'Seller')}
      />
    </div>
  );
};

export default ListingDetail;
